// cspell:ignore Couldn
import { Box, Flex, HStack, Spinner, Switch, Text } from "@chakra-ui/react";
import React, { useContext, useEffect, useState } from "react";
import {
  UseFieldArrayAppend,
  UseFieldArrayMove,
  UseFieldArrayRemove,
} from "react-hook-form";
import { HiOutlineRefresh } from "react-icons/hi";
import { HiOutlineExclamationCircle } from "react-icons/hi2";

import { Button, GradientSparklesIcon } from "../../../../components";
import { AiInterviewQuestion } from "../../../graphql";
import { AiInterviewerWizardContext } from "../../AiInterviewerWizard/AiInterviewerWizardContext";
import AiInterviewerGuideQuestions from "../AiInterviewerGuideQuestions";
import AiInterviewerSection from "../AiInterviewerSection";
import AiInterviewerSectionTitle from "../AiInterviewerSectionTitle";
import { AiInterviewerFormValues } from "../types";
import { bluePurpleGradient } from "./constants";
import GuideAssistantPanel from "./GuideAssistantPanel";
import useGuideAssistant from "./useGuideAssistant";
import useGuideAssistantPreviewState from "./useGuideAssistantPreviewState";

const GUIDE_MAIN_COLUMN_WIDTH = "720px";

type AiInterviewerGuideAssistantProps = {
  readOnly?: boolean;
  questions: AiInterviewQuestion[];
  appendQuestion: UseFieldArrayAppend<AiInterviewerFormValues, "questions">;
  removeQuestion: UseFieldArrayRemove;
  move: UseFieldArrayMove;
  pendingAcceptedSuggestionIds: ReadonlySet<string>;
  onAcceptSuggestion: (suggestionId: string) => void;
};

const AiInterviewerGuideAssistant: React.FC<
  AiInterviewerGuideAssistantProps
> = ({
  readOnly,
  questions,
  appendQuestion,
  removeQuestion,
  move,
  pendingAcceptedSuggestionIds,
  onAcceptSuggestion,
}) => {
  const [showAssistant, setShowAssistant] = useState(false);
  const { wizardOn, setGuideAssistantVisible } = useContext(
    AiInterviewerWizardContext
  );

  useEffect(() => {
    setGuideAssistantVisible?.(showAssistant);
    return () => setGuideAssistantVisible?.(false);
  }, [showAssistant, setGuideAssistantVisible]);

  const {
    loading,
    totalError,
    hasPartialError,
    questionSuggestions,
    isGuideStale,
    refreshSuggestions,
    ignoreSuggestion,
  } = useGuideAssistant();
  const {
    visibleQuestionSuggestions,
    visibleSuggestionCount,
    highlightedQuestionId,
    selectedSuggestionId,
    ignoringSuggestionId,
    applyingSuggestionId,
    previewsByQuestionId,
    suggestionConflictReasons,
    handleResolveField,
    handleResolveAllFields,
    handleSelectSuggestion,
    handleSelectQuestionTab,
    handleIgnorePreview,
    handleRefreshSuggestions,
    handleHoverQuestion,
  } = useGuideAssistantPreviewState({
    questions,
    questionSuggestions,
    pendingAcceptedSuggestionIds,
    refreshSuggestions,
    ignoreSuggestion,
    onAcceptSuggestion,
  });

  const panelVisible = showAssistant;
  const visibleHighlightedQuestionId = panelVisible
    ? highlightedQuestionId
    : null;
  const visiblePreviewsByQuestionId = panelVisible
    ? previewsByQuestionId
    : undefined;
  const panelState = loading
    ? "loading"
    : totalError
    ? "total-error"
    : visibleQuestionSuggestions.length > 0
    ? "ready"
    : "empty";

  const showToggle = (
    <Flex align="center" gap={2}>
      <Text fontSize="sm">Show</Text>
      <Switch
        variant="purple"
        isChecked={showAssistant}
        onChange={(event) => setShowAssistant(event.target.checked)}
      />
    </Flex>
  );

  return (
    <Flex
      w="100%"
      justifyContent="center"
      pt="4"
      alignItems="flex-start"
      gap={4}
    >
      <AiInterviewerSection
        w="100%"
        maxW={GUIDE_MAIN_COLUMN_WIDTH}
        p="6"
        pt="6"
        boxSizing="border-box"
        flexShrink={1}
      >
        <AiInterviewerSectionTitle
          title={`Interview guide (${questions.length})`}
        />
        {totalError ? (
          <Flex
            w="100%"
            mb={4}
            px={4}
            py={2}
            bg="yellow.50"
            border="1px solid"
            borderColor="yellow.400"
            borderRadius="md"
            alignItems="center"
            justifyContent="space-between"
            gap={3}
          >
            <HStack spacing={4} align="center" flex={1}>
              <HiOutlineExclamationCircle color="#B7791F" size={22} />
              <Text fontSize="sm" color="yellow.800" fontWeight="600">
                Couldn&apos;t analyze your guide.
              </Text>
              <Button
                size="xs"
                variant="newPrismTertiary"
                minW="auto"
                borderColor="accent.600"
                leftIcon={<HiOutlineRefresh />}
                onClick={handleRefreshSuggestions}
              >
                Try again
              </Button>
            </HStack>
            {showToggle}
          </Flex>
        ) : (
          <Flex
            w="100%"
            mb={4}
            px={4}
            py={2}
            background={bluePurpleGradient(0.1)}
            borderRadius="md"
            alignItems="center"
            justifyContent="space-between"
            position="relative"
            sx={{
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                padding: "1px",
                background: bluePurpleGradient(0.2),
                mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                maskComposite: "exclude",
                WebkitMaskComposite: "xor",
                pointerEvents: "none",
              },
            }}
          >
            <Flex align="center" gap={2}>
              {loading ? (
                <Spinner size="sm" color="blue.500" />
              ) : (
                <GradientSparklesIcon />
              )}
              <Text
                fontSize="sm"
                fontWeight="600"
                background={bluePurpleGradient(1)}
                backgroundClip="text"
                color="transparent"
              >
                {loading
                  ? "Loading suggestions. This can take up to a minute."
                  : showAssistant
                  ? `Showing ${visibleSuggestionCount} suggestions.`
                  : `We found ${visibleSuggestionCount} ways to improve this guide.`}
              </Text>
            </Flex>
            {showToggle}
          </Flex>
        )}

        <Box w="100%" pr={1}>
          <AiInterviewerGuideQuestions
            readOnly={readOnly}
            questions={questions}
            appendQuestion={appendQuestion}
            removeQuestion={removeQuestion}
            move={move}
            highlightedQuestionId={visibleHighlightedQuestionId}
            previewsByQuestionId={visiblePreviewsByQuestionId}
            onResolvePreviewField={handleResolveField}
            onResolvePreviewAll={handleResolveAllFields}
            onIgnorePreview={handleIgnorePreview}
            applyingSuggestionId={applyingSuggestionId}
            ignoringSuggestionId={ignoringSuggestionId}
          />
        </Box>
      </AiInterviewerSection>

      {panelVisible && (
        <GuideAssistantPanel
          panelState={panelState}
          hasPartialError={hasPartialError}
          questionSuggestions={visibleQuestionSuggestions}
          selectedSuggestionId={selectedSuggestionId}
          ignoringSuggestionId={ignoringSuggestionId}
          disabledSuggestionReasons={suggestionConflictReasons}
          isGuideStale={isGuideStale}
          onSelectQuestion={handleSelectQuestionTab}
          onSelectSuggestion={handleSelectSuggestion}
          onRefreshSuggestions={handleRefreshSuggestions}
          onHoverQuestion={handleHoverQuestion}
          stickyTop={wizardOn ? "0px" : undefined}
        />
      )}
    </Flex>
  );
};

export default AiInterviewerGuideAssistant;
